import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { APShowMoreContainer } from "../../../components/APShowMoreContainer";
import { AutoCompleteOption } from "../../../components/form/APAutoComplete";
import { APSliderRangeFilter } from "../../../components/form/APSlider";
import {
  VehicleVariantFieldLabelMap,
  VehicleVariantValueFormatters,
} from "../../../helpers/vehicle";
import { VehicleSearchInputs } from "../index";

const isSlider = (value: unknown): value is APSliderRangeFilter => {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    ("value" in value || ("min" in value && "max" in value))
  );
};
const isSingleAutoComplete = (value: unknown): value is AutoCompleteOption => {
  return (
    typeof value === "object" &&
    value !== null &&
    "label" in value &&
    "id" in value
  );
};
const isMultipleAutoComplete = (
  value: unknown
): value is AutoCompleteOption[] => {
  return Array.isArray(value) && value.every((v) => isSingleAutoComplete(v));
};

const VehicleSearchFilterSummary = ({
  inputs,
  valueFormatters,
  fieldLabelMap,
  expanded,
  onExpandToggle,
}: {
  inputs: VehicleSearchInputs;
  valueFormatters: VehicleVariantValueFormatters;
  fieldLabelMap: VehicleVariantFieldLabelMap;
  expanded: boolean;
  onExpandToggle: () => void;
}) => {
  const buildSelectSummaryValueText = (options: AutoCompleteOption[]) => {
    return options.map((option) => option.label).join(" or ");
  };

  const buildSliderSummaryValueText = (
    slider: APSliderRangeFilter,
    valueFormatter?: (value: number) => string
  ) => {
    switch (slider.type) {
      case "any":
        return "Any";
      case "lte":
        return `${
          valueFormatter ? valueFormatter(slider.value) : slider.value
        } or less`;
      case "gte":
        return `${
          valueFormatter ? valueFormatter(slider.value) : slider.value
        } or more`;
      case "eq":
        return `${
          valueFormatter ? valueFormatter(slider.value) : slider.value
        }`;
      case "between":
        return `${
          valueFormatter ? valueFormatter(slider.min) : slider.min
        } to ${valueFormatter ? valueFormatter(slider.max) : slider.max}`;
    }
  };

  const buildSummaryText = (inputs: VehicleSearchInputs) => {
    const fieldTexts = [] as string[];
    const orderedKeys: (keyof VehicleSearchInputs)[] = [
      "priceRange",
      "year",
      "bodyType",
      "powerPlantType",
      "seatCapacity",
      "make",
      "evRange",
      "fuelEconomyRange",

      "ancapratingRange",
      "warrantyYearsRange",
      "segment",
      "transmission",

      "powerKwRange",
      "accelerationRange",
      "powerToWeightRange",
      "driveDescription",

      "minCargoVolumeRange",
      "towingUnbrakedKgRange",

      "fuelType",
      "co2CombinedRange",
    ];

    orderedKeys.forEach((key) => {
      const value = inputs[key];
      let valueText: string | undefined;

      if (isSingleAutoComplete(value)) {
        valueText = buildSelectSummaryValueText([value]);
      } else if (isMultipleAutoComplete(value)) {
        valueText =
          value.length > 0 ? buildSelectSummaryValueText(value) : undefined;
      } else if (isSlider(value)) {
        valueText =
          value.type !== "any"
            ? buildSliderSummaryValueText(
                value,
                valueFormatters[key as keyof VehicleVariantValueFormatters]
              )
            : undefined;
      }

      if (valueText) {
        fieldTexts.push(
          `${
            fieldLabelMap[key as keyof VehicleVariantFieldLabelMap]
          }: ${valueText}`
        );
      }
    });

    return (
      <Typography variant="titleSmall">
        {fieldTexts.map((text, index) => (
          <span key={index}>
            {text}
            {index < fieldTexts.length - 1 && <b> AND </b>}
          </span>
        ))}
      </Typography>
    );
  };

  return (
    <APShowMoreContainer
      collapsedSize={44}
      open={expanded}
      onToggle={onExpandToggle}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        {buildSummaryText(inputs)}
      </Box>
    </APShowMoreContainer>
  );
};

export default VehicleSearchFilterSummary;
